import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  createColumnHelper, getCoreRowModel, getPaginationRowModel, getSortedRowModel, useReactTable,
} from '@tanstack/react-table';
import {
  s, authHeaders, Toast, toGridSortField, toODataSortField,
} from './listPageStyles';
import { ListPageShell, Pagination, SearchableSelect, StatusBadge } from './ui';
import {
  useC360List, C360Table, ColumnChooser, buildSelectFields,
} from './c360';
import {
  INVOICE_API, CUSTOMER_API, REPRESENTATIVE_API, AGING_BUCKETS,
  AGING_BASE_FILTER, agingBucketFilter, overdueFilter, daysOverdueOf, bucketOf,
  fetchAging, fetchOverpaidInvoices, resolveNames, lookupLabelOf,
  fmtMoney, fmtDate, pick, toNum, bucketLabel,
} from './finance/allocationShared';

const muted = 'var(--text-secondary, #6b7280)';
const strong = 'var(--card-text-strong, #0b2046)';
const danger = 'var(--danger, #e2445c)';
const border = '1px solid var(--border-color, rgba(15,23,42,0.10))';
const LOOKUP_PAGE = 20;
const getRows = (data) => (Array.isArray(data) ? data : (data?.value || []));

// Kova yaşlandıkça kızarır (0 = vadesi gelmemiş → nötr).
const BUCKET_TONE = {
  0: 'var(--text-secondary, #6b7280)',
  1: 'var(--success, #16a34a)',
  2: 'var(--warn, #fdab3d)',
  3: '#f97316',
  4: 'var(--danger, #e2445c)',
};

const st = {
  buckets: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12,
    padding: '12px 14px', marginBottom: 12, border, borderRadius: 10, background: 'var(--bg-card, #fff)',
  },
  bLabel: { color: muted, fontSize: 11, fontWeight: 800, textTransform: 'uppercase' },
  bValue: { fontSize: 18, fontWeight: 800 },
  bHint: { color: muted, fontSize: 11, marginTop: 2 },
  filters: { display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' },
  lookupBox: { minWidth: 240 },
  tableWrap: { border, borderRadius: 10, overflow: 'auto', background: 'var(--bg-card, #fff)' },
  table: { width: '100%', minWidth: 900, borderCollapse: 'collapse', fontSize: 13 },
  th: {
    position: 'sticky', top: 0, zIndex: 1, textAlign: 'right', padding: '10px 8px',
    background: 'var(--bg-subtle, #f9fafb)', color: muted, fontSize: 11, fontWeight: 800,
    textTransform: 'uppercase', whiteSpace: 'nowrap', borderBottom: border,
  },
  thLeft: { textAlign: 'left' },
  td: {
    padding: '9px 8px', borderBottom: '1px solid var(--border-color, rgba(15,23,42,0.06))',
    textAlign: 'right', color: 'var(--text-primary, #111827)', whiteSpace: 'nowrap',
  },
  tdLeft: { textAlign: 'left' },
  open: { fontWeight: 800, color: 'var(--accent, #0073ea)' },
  num: { textAlign: 'right' },
  overpaid: {
    marginBottom: 12, padding: '12px 14px', borderRadius: 10,
    background: 'rgba(226,68,92,0.08)', border: `1px solid ${danger}`,
  },
  overpaidTitle: { color: danger, fontWeight: 800, fontSize: 13, margin: '0 0 8px' },
  linkBtn: {
    background: 'none', border: 'none', padding: 0, cursor: 'pointer',
    color: 'var(--accent, #0073ea)', fontWeight: 700, fontSize: 13, textAlign: 'left',
  },
};

/**
 * YAŞLANDIRMA (#Faz2, spec §7.3) — menü 9750, /finance/aging.
 *
 * ══ İKİ KAYNAK, TEK GERÇEK ════════════════════════════════════════════════════════════════════
 * • KOVA TOPLAMLARI + MÜŞTERİ BAZLI GÖRÜNÜM → /api/reports/aging. Bu uç DÜZ JSON döner (OData
 *   değil): sayfalanamaz, sıralanamaz, huni takılamaz. Ama zaten TOPLULAŞTIRMA döndürüyor —
 *   sayfalanacak bir şey yok.
 * • FATURA LİSTESİ → Invoice OData'sı (c360 / useC360List). Rapor ucundaki `Invoices` dizisi
 *   TÜM açık faturaları tek atımda indiriyordu; binlerce faturalı bir tenant'ta bu, ekranı
 *   açılışta kilitler. c360 sunucuda sayfalar/sıralar ve kolon hunileri verir.
 *
 * İkisi de AYNI KOLONU okur (Invoice.OpenAmount — COMPUTED PERSISTED = Net − Eşleşen − İade);
 * rapor kendi toplamını TUTMAZ. Bu yüzden iki kaynak arasında drift İMKÂNSIZDIR: kova toplamları
 * ile listenin gösterdiği açık tutarlar aynı veriden gelir. Kova sınırları (`agingBucketFilter`)
 * backend'in `BucketOf`u ile birebirdir.
 *
 * `overpaid` (negatif açık = VERİ HATASI) faturalar her iki kaynakta da GÖRÜNMEZ (ikisi de
 * OpenAmount > 0 filtreler) → ayrıca çekilip üstte KIRMIZI uyarı listesinde gösterilir (§10.10).
 */
export function AgingReportPage() {
  const navigate = useNavigate();

  const [data, setData] = useState({ totals: {}, byCustomer: [], asOf: null });
  const [reportLoading, setReportLoading] = useState(true);
  const [overpaid, setOverpaid] = useState([]);
  const [custMap, setCustMap] = useState({});
  const [repMap, setRepMap] = useState({});
  const [overpaidCustMap, setOverpaidCustMap] = useState({});
  const [toast, setToast] = useState(null);
  const [columnVisibility, setColumnVisibility] = useState({});

  // Filtreler
  const [customerId, setCustomerId] = useState('');
  const [customerLabel, setCustomerLabel] = useState('');
  const [representativeId, setRepresentativeId] = useState('');
  const [repLabel, setRepLabel] = useState('');
  const [onlyOverdue, setOnlyOverdue] = useState(true); // spec: "vadesi geçmiş açık faturalar"
  const [bucketFilter, setBucketFilter] = useState(null); // kova rozetine tıklayınca daralt
  const [view, setView] = useState('invoice'); // 'invoice' (c360) | 'customer' (rapor toplulaştırması)

  // Müşteri bazlı görünüm rapor ucundan gelir (toplulaştırma) → client-side sayfalanır.
  const [custPageSize, setCustPageSize] = useState(25);
  const [custPage, setCustPage] = useState(1);

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  // ── FATURA LİSTESİ (c360 · Invoice OData) ───────────────────────────────────────────────────
  // Kolon id'leri OData alan adlarının camelCase'idir (toODataSortField başlığa tıklayınca onları
  // geri PascalCase'e çevirir) — `invoiceNo` gibi uydurma bir id, $orderby=InvoiceNo → 400 verirdi.
  const columnFilterMeta = useMemo(() => ({
    textId: { type: 'text', odataField: 'TextId' },
    documentDate: { type: 'date', odataField: 'DocumentDate' },
    dueDate: { type: 'date', odataField: 'DueDate' },
    netAmount: { type: 'number', odataField: 'NetAmount' },
    openAmount: { type: 'number', odataField: 'OpenAmount' },
  }), []);
  const searchFields = useMemo(() => ['TextId', 'PrintedNo'], []);

  // Toolbar (müşteri/plasiyer) + kova kısayolu + "yalnız vadesi geçmiş" → OData $filter.
  // Kullanıcı bunları huniden temizleyemez; kova rozetine tekrar tıklamak kaldırır.
  const baseFilter = useMemo(() => {
    const parts = [AGING_BASE_FILTER];
    if (customerId) parts.push(`CustomerId eq ${customerId}`);
    if (representativeId) parts.push(`RepresentativeId eq ${representativeId}`);
    if (onlyOverdue) parts.push(overdueFilter());
    if (bucketFilter !== null) parts.push(agingBucketFilter(bucketFilter));
    return parts.join(' and ');
  }, [customerId, representativeId, onlyOverdue, bucketFilter]);

  // Gecikme/Yaş Kovası hücreleri DueDate'ten türetilir; Müşteri/Plasiyer FK'dan çözülür → hepsi $select'te.
  const selectFields = useMemo(() => buildSelectFields({
    columnFilterMeta,
    columnVisibility,
    always: ['Id', 'CustomerId', 'RepresentativeId', 'TextId', 'DocumentDate', 'DueDate', 'NetAmount', 'OpenAmount'],
  }), [columnVisibility, columnFilterMeta]);

  const list = useC360List({
    apiBase: INVOICE_API,
    baseFilter,
    columnFilterMeta,
    searchFields,
    // En eski vade EN ÜSTTE = en çok geciken en üstte (rapordaki "daysOverdue desc" sıralamasının
    // sunucu-taraflı karşılığı; DaysOverdue diye bir kolon YOKTUR, türetilmiştir).
    defaultSortField: 'DueDate',
    defaultSortDir: 'asc',
    selectFields,
    // Müşteri bazlı görünümdeyken fatura listesi çekilmez (rapor toplulaştırması gösterilir).
    ready: view === 'invoice',
    onError: (msg) => showToast('error', msg),
  });

  const {
    rows, totalCount, loading,
    filterInput, setFilterInput, filterText, applyFilterText,
    columnFilters, setColumnFilter, clearColumnFilter,
    sortField, sortDir, applySort, pageSize, setPageSize, currentPage, setCurrentPage,
    fetchData,
  } = list;

  // ── KOVA TOPLAMLARI + MÜŞTERİ BAZLI (rapor ucu) ─────────────────────────────────────────────
  const loadReport = useCallback(async () => {
    setReportLoading(true);
    try {
      const res = await fetchAging({ customerId, representativeId });
      setData({
        asOf: pick(res, 'asOf', 'AsOf'),
        totals: pick(res, 'totals', 'Totals') || {},
        byCustomer: pick(res, 'byCustomer', 'ByCustomer') || [],
      });
    } catch (e) {
      showToast('error', e.message || 'Yaşlandırma raporu alınamadı');
      setData({ totals: {}, byCustomer: [], asOf: null });
    } finally {
      setReportLoading(false);
    }
  }, [customerId, representativeId]);

  useEffect(() => { loadReport(); }, [loadReport]);

  const refreshAll = useCallback(() => { loadReport(); fetchData(); }, [loadReport, fetchData]);

  // Veri hatası kuyruğu (negatif açık tutar) — filtrelerden bağımsız, her zaman görünür.
  useEffect(() => {
    fetchOverpaidInvoices().then(setOverpaid).catch(() => setOverpaid([]));
  }, []);

  // Görünen satırların müşteri / plasiyer adlarını topluca çöz (Invoice yalnız FK taşır).
  useEffect(() => {
    const ids = rows.map((r) => r.customerId).filter((id) => id && !custMap[id]);
    if (ids.length === 0) return;
    resolveNames(CUSTOMER_API, ids, 'Title').then((m) => {
      if (Object.keys(m).length) setCustMap((prev) => ({ ...prev, ...m }));
    });
  }, [rows, custMap]);

  useEffect(() => {
    const ids = rows.map((r) => r.representativeId).filter((id) => id && !repMap[id]);
    if (ids.length === 0) return;
    resolveNames(REPRESENTATIVE_API, ids, 'Name').then((m) => {
      if (Object.keys(m).length) setRepMap((prev) => ({ ...prev, ...m }));
    });
  }, [rows, repMap]);

  useEffect(() => {
    const ids = overpaid.map((i) => i.customerId).filter((id) => id && !overpaidCustMap[id]);
    if (ids.length === 0) return;
    resolveNames(CUSTOMER_API, ids, 'Title').then((m) => {
      if (Object.keys(m).length) setOverpaidCustMap((prev) => ({ ...prev, ...m }));
    });
  }, [overpaid, overpaidCustMap]);

  // --- Lookup fetch'leri (SearchableSelect) ---
  const fetchLookup = useCallback(async (api, nameField, query, skip = 0) => {
    const term = (query || '').replace(/'/g, "''").toLowerCase();
    const filter = term
      ? `contains(tolower(TextId),'${term}') or contains(tolower(${nameField}),'${term}')`
      : '';
    let url = `${api}?$top=${LOOKUP_PAGE}&$skip=${skip}&$orderby=TextId`;
    if (filter) url += `&$filter=${encodeURIComponent(filter)}`;
    const r = await fetch(url, { headers: authHeaders() });
    if (!r.ok) return [];
    const lookupRows = getRows(await r.json());
    return lookupRows.map((c) => ({ value: pick(c, 'id', 'Id'), label: lookupLabelOf(c, nameField) }));
  }, []);

  const fetchCustomers = useCallback((q, skip) => fetchLookup(CUSTOMER_API, 'Title', q, skip), [fetchLookup]);
  const fetchReps = useCallback((q, skip) => fetchLookup(REPRESENTATIVE_API, 'Name', q, skip), [fetchLookup]);

  // --- Müşteri bazlı satırlar (rapor toplulaştırması) ---
  const customerRows = useMemo(() => data.byCustomer.map((c) => ({
    customerId: pick(c, 'customerId', 'CustomerId'),
    customerName: pick(c, 'customerName', 'CustomerName') || '-',
    totalOpen: toNum(pick(c, 'totalOpen', 'TotalOpen')),
    notDue: toNum(pick(c, 'notDue', 'NotDue')),
    days1To30: toNum(pick(c, 'days1To30', 'Days1To30')),
    days31To60: toNum(pick(c, 'days31To60', 'Days31To60')),
    days61To90: toNum(pick(c, 'days61To90', 'Days61To90')),
    days90Plus: toNum(pick(c, 'days90Plus', 'Days90Plus')),
    oldestOverdueDays: toNum(pick(c, 'oldestOverdueDays', 'OldestOverdueDays')),
    invoiceCount: toNum(pick(c, 'invoiceCount', 'InvoiceCount')),
  })), [data.byCustomer]);

  const custPageRows = useMemo(
    () => customerRows.slice((custPage - 1) * custPageSize, custPage * custPageSize),
    [customerRows, custPage, custPageSize],
  );

  useEffect(() => { setCustPage(1); }, [customerId, representativeId, view]);

  const totals = data.totals;
  const totalOpen = toNum(pick(totals, 'totalOpen', 'TotalOpen'));

  // ── Kolonlar (fatura bazlı görünüm) ─────────────────────────────────────────────────────────
  const columnHelper = createColumnHelper();
  const columns = useMemo(() => [
    columnHelper.display({
      id: 'customer', header: 'Müşteri', enableSorting: false, enableHiding: false,
      cell: ({ row }) => (row.original.customerId ? (custMap[row.original.customerId] || '…') : '-'),
    }),
    columnHelper.accessor('textId', {
      header: 'Fatura No', meta: { filterType: 'text' },
      cell: ({ row }) => (
        <button style={st.linkBtn} onClick={(e) => { e.stopPropagation(); navigate(`/invoices/${row.original.id}`); }}>
          {row.original.textId || '-'}
        </button>
      ),
    }),
    columnHelper.accessor('documentDate', {
      header: 'Tarih', meta: { filterType: 'date' },
      cell: (info) => fmtDate(info.getValue()),
    }),
    columnHelper.accessor('dueDate', {
      header: 'Vade', meta: { filterType: 'date' },
      cell: (info) => fmtDate(info.getValue()),
    }),
    // TÜRETİLMİŞ (DB'de DaysOverdue/Bucket kolonu YOK) → sıralanamaz/filtrelenemez.
    // Daraltma kova rozetlerinden yapılır; sıralama vade tarihine göredir (aynı şey).
    columnHelper.display({
      id: 'daysOverdue', header: 'Gecikme (Gün)', enableSorting: false,
      cell: ({ row }) => {
        const d = daysOverdueOf(row.original.dueDate);
        return (
          <span style={{ ...st.num, color: d > 0 ? danger : muted, fontWeight: d > 0 ? 800 : 400 }}>
            {d > 0 ? d : '-'}
          </span>
        );
      },
    }),
    columnHelper.display({
      id: 'bucket', header: 'Yaş Kovası', enableSorting: false,
      cell: ({ row }) => {
        const b = bucketOf(daysOverdueOf(row.original.dueDate));
        return <span style={{ color: BUCKET_TONE[b], fontWeight: 700 }}>{bucketLabel(b)}</span>;
      },
    }),
    columnHelper.accessor('netAmount', {
      header: 'Fatura Tutarı', meta: { filterType: 'number' },
      cell: (info) => <span style={st.num}>{fmtMoney(info.getValue())}</span>,
    }),
    columnHelper.accessor('openAmount', {
      header: 'Açık Tutar', meta: { filterType: 'number' },
      cell: (info) => <span style={{ ...st.num, ...st.open }}>{fmtMoney(info.getValue())}</span>,
    }),
    columnHelper.display({
      id: 'representative', header: 'Plasiyer', enableSorting: false,
      cell: ({ row }) => (row.original.representativeId ? (repMap[row.original.representativeId] || '…') : '-'),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [custMap, repMap, navigate]);

  const sorting = useMemo(
    () => (sortField ? [{ id: toGridSortField(sortField), desc: sortDir === 'desc' }] : []),
    [sortField, sortDir],
  );
  const handleSortingChange = (updater) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    const first = next[0];
    if (!first) { applySort('DueDate', 'asc'); return; }
    applySort(toODataSortField(first.id), first.desc ? 'desc' : 'asc');
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting, columnVisibility, pagination: { pageIndex: currentPage - 1, pageSize } },
    onSortingChange: handleSortingChange,
    onColumnVisibilityChange: setColumnVisibility,
    manualSorting: true,
    manualPagination: true,
    pageCount: totalPages,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection: false,
    getRowId: (row) => String(row.id),
  });

  const isInvoiceView = view === 'invoice';

  const toolbarNode = (
    <div style={st.filters}>
      {isInvoiceView && (
        <>
          <input
            style={s.filterInput}
            type="text"
            placeholder="Fatura no ile ara..."
            value={filterInput}
            onChange={(e) => setFilterInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') applyFilterText(filterInput); }}
          />
          {filterText && (
            <button style={s.btnSecondary} onClick={() => { setFilterInput(''); applyFilterText(''); }}>Temizle</button>
          )}
        </>
      )}
      <div style={st.lookupBox}>
        <SearchableSelect
          value={customerId}
          selectedLabel={customerLabel}
          onChange={(v, item) => { setCustomerId(v || ''); setCustomerLabel(item?.label || ''); setCurrentPage(1); }}
          fetchOptions={fetchCustomers}
          placeholder="Müşteri ara…"
          pageSize={LOOKUP_PAGE}
        />
      </div>
      <div style={st.lookupBox}>
        <SearchableSelect
          value={representativeId}
          selectedLabel={repLabel}
          onChange={(v, item) => { setRepresentativeId(v || ''); setRepLabel(item?.label || ''); setCurrentPage(1); }}
          fetchOptions={fetchReps}
          placeholder="Plasiyer ara…"
          pageSize={LOOKUP_PAGE}
        />
      </div>
      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: strong, fontWeight: 600 }}>
        <input
          type="checkbox"
          checked={onlyOverdue}
          onChange={(e) => { setOnlyOverdue(e.target.checked); setCurrentPage(1); }}
        />
        Yalnız vadesi geçmiş
      </label>
      <button
        style={isInvoiceView ? s.btnPrimary : s.btnSecondary}
        onClick={() => setView('invoice')}
      >
        Fatura Bazlı
      </button>
      <button
        style={!isInvoiceView ? s.btnPrimary : s.btnSecondary}
        onClick={() => setView('customer')}
      >
        Müşteri Bazlı
      </button>
      {(customerId || representativeId || bucketFilter !== null) && (
        <button
          style={s.btnSecondary}
          onClick={() => {
            setCustomerId(''); setCustomerLabel('');
            setRepresentativeId(''); setRepLabel('');
            setBucketFilter(null);
            setCurrentPage(1);
          }}
        >
          Filtreleri Temizle
        </button>
      )}
      <button style={s.btnSecondary} onClick={refreshAll}>Yenile</button>
      {isInvoiceView && <ColumnChooser table={table} />}
    </div>
  );

  const footerNode = isInvoiceView ? (
    <Pagination
      currentPage={currentPage}
      totalCount={totalCount}
      pageSize={pageSize}
      rowCount={rows.length}
      onPageChange={setCurrentPage}
      onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
    />
  ) : (
    <Pagination
      currentPage={custPage}
      totalCount={customerRows.length}
      pageSize={custPageSize}
      rowCount={custPageRows.length}
      onPageChange={setCustPage}
      onPageSizeChange={(size) => { setCustPageSize(size); setCustPage(1); }}
    />
  );

  return (
    <div style={s.container}>
      <ListPageShell
        title="Yaşlandırma"
        subtitle={`Vadesi geçmiş açık faturalar${data.asOf ? ` · ${fmtDate(data.asOf)} itibarıyla` : ''}`}
        toolbar={toolbarNode}
        footer={footerNode}
      >
        {/* VERİ HATASI — negatif açık tutar (overpaid). Fazla ödeme faturaya değil avansa gitmeliydi. */}
        {overpaid.length > 0 && (
          <div style={st.overpaid}>
            <h4 style={st.overpaidTitle}>
              ⚠ Veri Hatası — Fazla Ödenmiş Fatura ({overpaid.length})
            </h4>
            <div style={{ fontSize: 12, color: muted, marginBottom: 8 }}>
              Açık tutarı <b>negatif</b> olan faturalar. Fazla ödeme faturaya değil avansa yazılmalıydı —
              eşleştirmeyi <b>Yeniden Eşleştir</b> ile düzeltin.
            </div>
            {overpaid.slice(0, 10).map((i) => (
              <div
                key={i.id}
                style={{
                  display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center',
                  padding: '6px 0', borderTop: '1px solid rgba(226,68,92,0.25)', fontSize: 13,
                }}
              >
                <button style={st.linkBtn} onClick={() => navigate(`/invoices/${i.id}`)}>{i.invoiceNo}</button>
                <span style={{ color: muted }}>{i.customerId ? (overpaidCustMap[i.customerId] || '…') : '-'}</span>
                <span style={{ color: muted }}>Fatura: {fmtMoney(i.netAmount)}</span>
                <span style={{ color: muted }}>Eşleşen: {fmtMoney(i.allocatedAmount)}</span>
                <b style={{ color: danger, marginLeft: 'auto' }}>Açık: {fmtMoney(i.openAmount)}</b>
                <StatusBadge tone="bad">Fazla Ödenmiş</StatusBadge>
              </div>
            ))}
          </div>
        )}

        {/* Yaş kovaları — tıklanınca liste o kovaya daralır (OData baseFilter kısayolu). */}
        <div style={st.buckets}>
          <div>
            <div style={st.bLabel}>Toplam Açık</div>
            <div style={{ ...st.bValue, color: strong }}>{fmtMoney(totalOpen)}</div>
            <div style={st.bHint}>{toNum(pick(totals, 'invoiceCount', 'InvoiceCount'))} açık fatura</div>
          </div>
          {AGING_BUCKETS.map((b) => {
            const val = toNum(pick(totals, b.totalsKey, b.totalsKey.charAt(0).toUpperCase() + b.totalsKey.slice(1)));
            const active = bucketFilter === b.bucket;
            return (
              <button
                key={b.key}
                type="button"
                onClick={() => {
                  setBucketFilter(active ? null : b.bucket);
                  // "Vadesi Gelmemiş" kovası ile "yalnız vadesi geçmiş" birbirini dışlar — biri
                  // seçilince diğeri kapanır, aksi halde kova her zaman BOŞ görünürdü.
                  if (!active && b.bucket === 0) setOnlyOverdue(false);
                  setCurrentPage(1);
                }}
                style={{
                  background: active ? 'var(--accent-soft, rgba(0, 115, 234,0.12))' : 'transparent',
                  border: active ? '1px solid var(--accent, #0073ea)' : '1px solid transparent',
                  borderRadius: 8, padding: '4px 8px', cursor: 'pointer', textAlign: 'left',
                }}
                title={`${b.label} — listeyi bu kovaya daralt`}
              >
                <div style={st.bLabel}>{b.label}</div>
                <div style={{ ...st.bValue, color: BUCKET_TONE[b.bucket] }}>{fmtMoney(val)}</div>
                <div style={st.bHint}>{active ? 'Filtre aktif' : 'Filtrelemek için tıklayın'}</div>
              </button>
            );
          })}
        </div>

        {isInvoiceView ? (
          <C360Table
            table={table}
            columnFilterMeta={columnFilterMeta}
            columnFilters={columnFilters}
            onApplyFilter={setColumnFilter}
            onClearFilter={clearColumnFilter}
            loading={loading}
            onRowDoubleClick={(row) => navigate(`/invoices/${row.id}`)}
            emptyText={onlyOverdue ? 'Vadesi geçmiş açık fatura yok.' : 'Açık fatura yok.'}
          />
        ) : (
          <div style={st.tableWrap}>
            <table style={st.table}>
              <thead>
                <tr>
                  <th style={{ ...st.th, ...st.thLeft }}>Müşteri</th>
                  <th style={st.th}>Açık Fatura</th>
                  <th style={st.th}>Vadesi Gelmemiş</th>
                  <th style={st.th}>0-30 Gün</th>
                  <th style={st.th}>31-60 Gün</th>
                  <th style={st.th}>61-90 Gün</th>
                  <th style={st.th}>90+ Gün</th>
                  <th style={st.th}>En Eski Gecikme</th>
                  <th style={st.th}>Toplam Açık</th>
                </tr>
              </thead>
              <tbody>
                {reportLoading && (
                  <tr><td colSpan={9} style={{ ...st.td, textAlign: 'center', color: muted, padding: 28 }}>Yükleniyor…</td></tr>
                )}
                {!reportLoading && custPageRows.length === 0 && (
                  <tr><td colSpan={9} style={{ ...st.td, textAlign: 'center', color: muted, padding: 28 }}>Açık fatura yok.</td></tr>
                )}
                {!reportLoading && custPageRows.map((c) => (
                  <tr key={c.customerId}>
                    <td style={{ ...st.td, ...st.tdLeft }}>{c.customerName}</td>
                    <td style={st.td}>{c.invoiceCount}</td>
                    <td style={{ ...st.td, color: BUCKET_TONE[0] }}>{fmtMoney(c.notDue)}</td>
                    <td style={{ ...st.td, color: BUCKET_TONE[1] }}>{fmtMoney(c.days1To30)}</td>
                    <td style={{ ...st.td, color: BUCKET_TONE[2] }}>{fmtMoney(c.days31To60)}</td>
                    <td style={{ ...st.td, color: BUCKET_TONE[3] }}>{fmtMoney(c.days61To90)}</td>
                    <td style={{ ...st.td, color: BUCKET_TONE[4], fontWeight: 800 }}>{fmtMoney(c.days90Plus)}</td>
                    <td style={{ ...st.td, color: c.oldestOverdueDays > 0 ? danger : muted, fontWeight: c.oldestOverdueDays > 0 ? 800 : 400 }}>
                      {c.oldestOverdueDays > 0 ? `${c.oldestOverdueDays} gün` : '-'}
                    </td>
                    <td style={{ ...st.td, ...st.open }}>{fmtMoney(c.totalOpen)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </ListPageShell>

      <Toast toast={toast} />
    </div>
  );
}

export default AgingReportPage;
